import { useCallback, useEffect, useRef } from "react";
import { annotate } from "rough-notation";
import { ANNOTATION_ACTION } from "@/features/tutor/constants";
import { useAnnotationStore } from "@/features/tutor/store/useAnnotationStore";
import convert from "color-convert";
import { debugLog } from "@/utils/debugLog";

export const useAnnotation = () => {
  const { annotations, setAnnotations } = useAnnotationStore();
  const questionContainerRef = useRef(null);
  const textNodesRef = useRef([]);
  const temporaryAnnotationsRef = useRef([]);

  const init = () => {
    textNodesRef.current = [];
    questionContainerRef.current = document.getElementById("slide-container");
    const findTextNodes = (node) => {
      if (node) {
        if (node.nodeType === Node.TEXT_NODE) {
          textNodesRef.current.push(node);
        }
        Array.from(node.childNodes).forEach(findTextNodes);
      }
    };
    findTextNodes(questionContainerRef.current);
  };

  const getColorWithOpacity = (color, opacity) => {
    // Handle hex colors
    if (color.startsWith("#")) {
      const rgb = convert.hex.rgb(color);
      return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
    }

    // Handle named colors
    if (color.match(/^[a-zA-Z]+$/)) {
      const rgb = convert.keyword.rgb(color);
      return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
    }

    // Default fallback to a light gray if color format is not recognized
    return `rgba(200, 200, 200, ${opacity})`;
  };

  const wrapElement = (
    element,
    action,
    duration,
    color,
    animate = true,
    tutor_action = true,
  ) => {
    debugLog("wrapElement", element, action, duration, color);
    if (action === ANNOTATION_ACTION.HIGHLIGHT) {
      const annotation = annotate(element, {
        type: action,
        color: getColorWithOpacity(color, 0.4),
        multiline: true,
        iterations: 1,
        duration: duration || 1000,
      });

      annotation.show();
      if (tutor_action) {
        setAnnotations([...annotations, { annotation, element }]);
      }
      temporaryAnnotationsRef.current.push({ annotation, element });
    } else if (action === ANNOTATION_ACTION.CIRCLE) {
      const annotation = annotate(element, {
        type: action,
        color: color,
        animate: animate,
        padding: 7,
      });
      annotation.show();
      if (tutor_action) {
        setAnnotations([...annotations, { annotation, element }]);
      }
      temporaryAnnotationsRef.current.push({ annotation, element });
    } else {
      const annotation = annotate(element, {
        type: action,
        color: color,
        animate: animate,
      });
      annotation.show();
      if (tutor_action) {
        setAnnotations([...annotations, { annotation, element }]);
      }
      temporaryAnnotationsRef.current.push({ annotation, element });
    }
  };

  const wrapText = (text, action, duration, color, tutor_action = true) => {
    init();
    //extract all the text nodes from the question container beforehand
    for (let i = 0; i < textNodesRef.current.length; i++) {
      const node = textNodesRef.current[i];
      const escapedText = text.replace(/[()]/g, "\\$&");
      const regex = new RegExp(escapedText, "gi");

      const matches = node.nodeValue.match(regex);
      if (matches) {
        const span = document.createElement("span");
        span.id = `${action}-${text}`;
        span.textContent = text;

        const newNode = document.createElement("span");
        node.nodeValue.split(regex).forEach((part, index, arr) => {
          if (part) newNode.appendChild(document.createTextNode(part));
          if (index < arr.length - 1) {
            newNode.appendChild(span.cloneNode(true));
          }
        });

        node.replaceWith(newNode);

        const element = document.getElementById(`${action}-${text}`);
        if (element) {
          wrapElement(element, action, duration, color, tutor_action);
        }
        break;
      }
    }
  };

  const isElementOutOfBounds = (element, container) => {
    if (!element || !container) return false;

    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const outOfBounds =
      elementRect.bottom <= containerRect.top ||
      elementRect.top >= containerRect.bottom;
    debugLog("isElementOutOfBounds", element, container, outOfBounds);
    return outOfBounds;
  };

  const getClosestContainer = (element, containers) => {
    let current = element;
    while (current) {
      if (containers.includes(current)) return current;
      current = current.parentElement;
    }
    return null;
  };

  const reApplyAnnotations = () => {
    if (!annotations || annotations.length === 0) return;
    // console.log("reApplying annotations", annotations);

    // Get all scrollable containers
    const containers = [
      document.querySelector(".practice-problem"),
      document.getElementById("slide-container"),
    ].filter(Boolean);

    annotations.forEach(({ annotation, element }) => {
      if (!annotation || !element) return;

      // Find the closest container that contains this element
      const container = getClosestContainer(element, containers);

      if (container) {
        if (!isElementOutOfBounds(element, container)) {
          annotation.animate = false;
          annotation.hide();
          annotation.show();
        } else {
          annotation.hide();
        }
      }
    });
  };

  const handleScroll = useCallback(() => {
    reApplyAnnotations();
  }, [annotations]);

  useEffect(() => {
    const containers = [
      document.getElementById("slide-container"),
      document.querySelector(".practice-problem"),
    ].filter(Boolean);

    const handleScroll = () => {
      reApplyAnnotations();
    };

    containers.forEach((container) => {
      container.addEventListener("scroll", handleScroll);
    });

    return () => {
      containers.forEach((container) => {
        container.removeEventListener("scroll", handleScroll);
      });
    };
  }, [handleScroll]);

  return {
    wrapText,
    wrapElement,
    annotations,
    reApplyAnnotations,
  };
};
